import React, {FC, useContext} from 'react';
import {withLocalization} from 'epic-localization/src/context/LocalizationContext';

import {withStyles} from '@material-ui/core';
import {
    ListSubheader,
    MenuItem,
    Box,
    Popover,
    Button,
    PopoverProps,
    ButtonProps
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import Typography from '@material-ui/core/Typography';

import useCSVExport, {PrepareDataFunction, UseCSVExportParams} from '../export/useCSVExport';
import {Formatter} from '../../../../../utils/CSVUtils';
import {PDFExportContext, PageOptionsProps} from '../export/PDFExportProvider';
import Tooltip from '../../../../../components/common/Tooltip';

const CODES = {
    downloadTitle: 'epic.affiliate.fortnite_games.widget_download.title',
    downloadPDF: 'epic.affiliate.fortnite_games.widget_download.pdf',
    downloadCSV: 'epic.affiliate.fortnite_games.widget_download.csv',
    tooltipTitle: 'epic.affiliate.fortnite_games.widget_download.tooltip'
};

export type WidgetDownloadButtonProps = UseCSVExportParams & {
    areaId: string;
    getMessage: (code: string) => string;
    csvValueFormatter?: Formatter;
    disabled?: boolean;
    prepareCSVData?: PrepareDataFunction;
    pageOptions?: PageOptionsProps;
};

const StyledButton = withStyles<string, {}, ButtonProps>(theme => ({
    root: {
        margin: 0
    },
    startIcon: {
        marginRight: 0
    }
}))(Button);

const StyledPopover = withStyles<string, {}, PopoverProps>(theme => ({
    paper: {
        // @ts-ignore
        backgroundColor: theme.palette.background.overlays,
        paddingBottom: theme.spacing(2)
    }
}))(Popover);

const StyledListSubheader = withStyles<string, {}, PopoverProps>(theme => ({
    root: {
        padding: theme.spacing(0, 4)
    }
}))(ListSubheader);

const WidgetDownloadButton: FC<WidgetDownloadButtonProps> = ({
    data,
    fileName: fileNameProps,
    areaId,
    pageOptions,
    getMessage,
    disabled,
    prepareCSVData,
    csvValueFormatter
}: WidgetDownloadButtonProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const fileName = `${fileNameProps}-${new Date().toLocaleDateString()}`;

    const downloadCSV = useCSVExport({
        data,
        fileName,
        prepareData: prepareCSVData,
        formatter: csvValueFormatter
    });

    const {onDownload: downloadPDF} = useContext(PDFExportContext);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDownloadPDF = () => {
        downloadPDF({name: fileName, options: pageOptions, id: [areaId]});
        handleClose();
    };

    const handleDownloadCSV = () => {
        downloadCSV();
        handleClose();
    };

    return (
        <>
            <Tooltip placement="top" title={getMessage(CODES.tooltipTitle)}>
                <Box display="flex" alignItems="center">
                    <StyledButton
                        variant="text"
                        component="div"
                        color="primary"
                        disabled={disabled}
                        startIcon={<GetAppIcon viewBox="0 0 22 24" />}
                        onClick={handleOpen}
                    />
                </Box>
            </Tooltip>

            <StyledPopover
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}>
                <StyledListSubheader>
                    <Typography variant="overline">{getMessage(CODES.downloadTitle)}</Typography>
                </StyledListSubheader>
                <MenuItem onClick={handleDownloadPDF}>{getMessage(CODES.downloadPDF)}</MenuItem>
                <MenuItem onClick={handleDownloadCSV}>{getMessage(CODES.downloadCSV)}</MenuItem>
            </StyledPopover>
        </>
    );
};

export default withLocalization(WidgetDownloadButton);
